import React, { useEffect, useMemo, useState } from 'react';
import '../css/accounting.css';
import { getHiddenForecastAccounts, normalizeForecastAccountName } from '../utils/forecastVisibility';

const formatCurrency = (value) => {
  const number = Number(value || 0);
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(number);
};

const normalizeStatementStartDay = (value, fallback = 1) => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) {
    return fallback;
  }
  return Math.max(1, Math.min(31, parsed));
};

const formatMonthKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const resolveStatementMonthForDate = (date, statementStartDay = 1) => {
  const normalizedStartDay = normalizeStatementStartDay(statementStartDay, 1);
  const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const effectiveStartDay = Math.min(normalizedStartDay, lastDayOfMonth);

  if (date.getDate() >= effectiveStartDay) {
    return formatMonthKey(new Date(date.getFullYear(), date.getMonth() + 1, 1));
  }

  return formatMonthKey(date);
};

const getCurrentCalendarMonthKey = () => formatMonthKey(new Date());

function Dashboard({ onboardingMonth = '' }) {
  const [transactions, setTransactions] = useState([]);
  const [credits, setCredits] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [accountForecast, setAccountForecast] = useState([]);
  const [forecastYear, setForecastYear] = useState(new Date().getFullYear());
  const [transactionMonth, setTransactionMonth] = useState(getCurrentCalendarMonthKey());
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [entryType, setEntryType] = useState('credit');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [creditId, setCreditId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [statementMonth, setStatementMonth] = useState(getCurrentCalendarMonthKey());
  const [statusMessage, setStatusMessage] = useState('');
  const [hiddenForecastAccounts, setHiddenForecastAccounts] = useState([]);
  const [hasInitializedCreditCycleMonth, setHasInitializedCreditCycleMonth] = useState(false);

  const user = localStorage.getItem('loggedInUsername');
  const currentYear = new Date().getFullYear();

  const minForecastYear = useMemo(() => {
    const normalized = String(onboardingMonth || '').trim();
    const yearValue = Number.parseInt(normalized.slice(0, 4), 10);
    if (Number.isNaN(yearValue)) {
      return currentYear;
    }
    return yearValue;
  }, [onboardingMonth, currentYear]);

  const maxForecastYear = useMemo(() => Math.max(minForecastYear, currentYear + 1), [minForecastYear, currentYear]);

  const forecastYears = useMemo(() => {
    const years = [];
    for (let yearValue = minForecastYear; yearValue <= maxForecastYear; yearValue += 1) {
      years.push(yearValue);
    }
    return years;
  }, [minForecastYear, maxForecastYear]);

  const getPreferredCreditId = (creditsList) => {
    const preferred = creditsList.find((credit) => String(credit.name || '').trim().toLowerCase() === 'barclaycard');
    if (preferred) {
      return String(preferred.id);
    }
    return creditsList.length > 0 ? String(creditsList[0].id) : '';
  };

  const sortAccountsByPreference = (accountsList) => {
    const rank = (name) => {
      const normalized = String(name || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');

      if (normalized.includes('current')) {
        return 0;
      }
      if (normalized === 'rds' || normalized.includes('rainyday') || normalized.includes('rainydaysaver')) {
        return 3;
      }
      if (normalized.includes('regular') || normalized === 'saver' || normalized.includes('regularsaver')) {
        return 1;
      }
      if (normalized.includes('investmentisa') || normalized.includes('investment')) {
        return 2;
      }

      return 4;
    };

    return [...accountsList].sort((left, right) => {
      const rankDiff = rank(left.name) - rank(right.name);
      if (rankDiff !== 0) {
        return rankDiff;
      }
      return String(left.name || '').localeCompare(String(right.name || ''));
    });
  };

  const buildBalanceLookup = (balances) => {
    const lookup = {};
    Object.entries(balances || {}).forEach(([accountName, amount]) => {
      lookup[normalizeForecastAccountName(accountName)] = amount;
    });
    return lookup;
  };

  const getStatementMonthForCredit = (credit) => resolveStatementMonthForDate(new Date(), credit?.statement_start_day || 1);

  const handleCreditSelection = (value) => {
    setCreditId(value);
    const selectedCredit = credits.find((credit) => String(credit.id) === String(value));
    if (!selectedCredit) {
      return;
    }

    const cycleMonth = getStatementMonthForCredit(selectedCredit);
    setStatementMonth(cycleMonth);
    setTransactionMonth(cycleMonth);
  };

  useEffect(() => {
    setHiddenForecastAccounts(getHiddenForecastAccounts(user));
  }, [user]);

  const visibleForecastAccounts = useMemo(() => {
    const hidden = new Set(hiddenForecastAccounts);
    return accounts.filter((account) => !hidden.has(normalizeForecastAccountName(account.name)));
  }, [accounts, hiddenForecastAccounts]);

  const loadTransactions = async (token, month) => {
    const response = await fetch('/api/accounting/transactions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ user, limit: 9999, statement_month: month })
    });

    if (!response.ok) {
      throw new Error('Failed to load transactions');
    }

    return response.json();
  };

  const loadCredits = async (token) => {
    const response = await fetch('/api/accounting/credits', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ user })
    });

    if (!response.ok) {
      throw new Error('Failed to load credits');
    }

    return response.json();
  };

  const loadAccounts = async (token) => {
    const response = await fetch('/api/accounting/accounts', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ user })
    });

    if (!response.ok) {
      throw new Error('Failed to load accounts');
    }

    return response.json();
  };

  const loadAccountForecast = async (token, yearValue) => {
    const response = await fetch('/api/accounting/forecast/accounts', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        user,
        year: yearValue
      })
    });

    if (!response.ok) {
      throw new Error('Failed to load account forecast');
    }

    return response.json();
  };

  useEffect(() => {
    const loadDashboard = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setErrorMessage('');

        const [transactionData, creditsData, forecastData, accountsData] = await Promise.all([
          loadTransactions(token, transactionMonth),
          loadCredits(token),
          loadAccountForecast(token, forecastYear),
          loadAccounts(token)
        ]);

        const creditsList = Array.isArray(creditsData) ? creditsData : [];
        const accountsList = sortAccountsByPreference(Array.isArray(accountsData) ? accountsData : []);
        setTransactions(Array.isArray(transactionData) ? transactionData : []);
        setCredits(creditsList);
        setAccountForecast(Array.isArray(forecastData.forecast) ? forecastData.forecast : []);
        setAccounts(accountsList);

        let nextCreditId = creditId;
        if (!creditId && creditsList.length > 0) {
          nextCreditId = getPreferredCreditId(creditsList);
          setCreditId(nextCreditId);
        }

        if (!hasInitializedCreditCycleMonth && creditsList.length > 0) {
          const activeCredit = creditsList.find((credit) => String(credit.id) === String(nextCreditId))
            || creditsList.find((credit) => String(credit.name || '').trim().toLowerCase() === 'barclaycard')
            || creditsList[0];
          if (activeCredit) {
            const resolvedMonth = getStatementMonthForCredit(activeCredit);
            setTransactionMonth(resolvedMonth);
            setStatementMonth(resolvedMonth);
            setHasInitializedCreditCycleMonth(true);
          }
        }
        if (!accountId && accountsList.length > 0) {
          setAccountId(String(accountsList[0].id));
        }
      } catch (error) {
        setErrorMessage('Unable to load your accounting dashboard.');
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [forecastYear, transactionMonth]);

  useEffect(() => {
    if (entryType === 'debit' && !accountId && accounts.length > 0) {
      setAccountId(String(accounts[0].id));
    }
    if (entryType === 'credit' && !creditId && credits.length > 0) {
      const preferredId = getPreferredCreditId(credits);
      handleCreditSelection(preferredId);
    }
  }, [entryType, accounts, credits, accountId, creditId]);

  useEffect(() => {
    if (transactionMonth) {
      setStatementMonth(transactionMonth);
    }
  }, [transactionMonth]);

  useEffect(() => {
    if (forecastYear < minForecastYear) {
      setForecastYear(minForecastYear);
      return;
    }
    if (forecastYear > maxForecastYear) {
      setForecastYear(maxForecastYear);
    }
  }, [forecastYear, minForecastYear, maxForecastYear]);

  const handleAddTransaction = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    try {
      const selectedCredit = credits.find((credit) => String(credit.id) === String(creditId));
      const selectedAccount = accounts.find((account) => String(account.id) === String(accountId));
      const response = await fetch('/api/accounting/transactions/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user,
          entry_type: entryType,
          amount,
          description,
          source: entryType === 'debit' ? (selectedAccount ? selectedAccount.name : '') : (selectedCredit ? selectedCredit.name : ''),
          credit_id: entryType === 'credit' ? (creditId || null) : null,
          account_id: entryType === 'debit' ? (accountId || null) : null,
          statement_month: statementMonth || null
        })
      });

      if (response.ok) {
        setAmount('');
        setDescription('');
        setStatusMessage('Transaction logged.');
        setEntryType('credit');
        setCreditId('');
        setAccountId('');
        const [updatedTransactions, refreshedCredits, refreshedForecast] = await Promise.all([
          loadTransactions(token, transactionMonth),
          loadCredits(token),
          loadAccountForecast(token, forecastYear)
        ]);
        setTransactions(Array.isArray(updatedTransactions) ? updatedTransactions : []);
        setCredits(Array.isArray(refreshedCredits) ? refreshedCredits : []);
        setAccountForecast(Array.isArray(refreshedForecast.forecast) ? refreshedForecast.forecast : []);
      } else {
        setStatusMessage('Failed to add transaction.');
      }
    } catch (error) {
      setStatusMessage('Error adding transaction.');
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    try {
      const response = await fetch('/api/accounting/transactions/delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user,
          transaction_id: transactionId
        })
      });

      if (response.ok) {
        setStatusMessage('Transaction deleted.');
        const [updatedTransactions, refreshedCredits, refreshedForecast] = await Promise.all([
          loadTransactions(token, transactionMonth),
          loadCredits(token),
          loadAccountForecast(token, forecastYear)
        ]);
        setTransactions(Array.isArray(updatedTransactions) ? updatedTransactions : []);
        setCredits(Array.isArray(refreshedCredits) ? refreshedCredits : []);
        setAccountForecast(Array.isArray(refreshedForecast.forecast) ? refreshedForecast.forecast : []);
      } else {
        setStatusMessage('Failed to delete transaction.');
      }
    } catch (error) {
      setStatusMessage('Error deleting transaction.');
    }
  };

  const sortedTransactions = useMemo(() => {
    return [...transactions].sort((a, b) => b.occurred_at - a.occurred_at);
  }, [transactions]);

  const parsedAmount = Number(amount);
  const canQuickAdd = Number.isFinite(parsedAmount)
    && parsedAmount !== 0
    && (entryType === 'credit' ? Boolean(creditId) : Boolean(accountId));

  return (
    <div className="lib-page">
      <section className="accounting-page">
        <div className="section-header">
          <div>
            <h2>Financial Dashboard</h2>
            <p className="section-subtitle">Fast transaction entry with balances and activity in one view.</p>
          </div>
          <button 
            className="login-button" 
            type="button" 
            onClick={() => setIsQuickAddOpen((prev) => !prev)}
            style={{ whiteSpace: 'nowrap', height: 'fit-content' }}
          >
            {isQuickAddOpen ? 'Hide quick add' : 'Quick add'}
          </button>
        </div>

        {loading && <div className="loading-state">Building your dashboard...</div>}
        {!loading && errorMessage && <div className="loading-state">{errorMessage}</div>}

        {!loading && !errorMessage && (
          <>
            {isQuickAddOpen && (
              <section className="panel panel-secondary">
                <div className="section-header">
                  <div>
                    <h3>Quick Add Transaction</h3>
                    <p className="panel-sub">Add a transaction without leaving the dashboard.</p>
                  </div>
                </div>
                <form
                  className="form-grid"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleAddTransaction();
                  }}
                >
                  <div className="form-row">
                    <label>
                      Type
                      <select value={entryType} onChange={(e) => setEntryType(e.target.value)}>
                        <option value="debit">Debit</option>
                        <option value="credit">Credit</option>
                      </select>
                    </label>
                    <label>
                      Amount
                      <input
                        type="number"
                        step="0.01"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0.00"
                      />
                    </label>
                    <label>
                      Description
                      <input
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Optional"
                      />
                    </label>
                    {entryType === 'credit' ? (
                      <label>
                        Credit account
                        <select value={creditId} onChange={(e) => handleCreditSelection(e.target.value)}>
                          <option value="">Select credit</option>
                          {credits.map((credit) => (
                            <option key={credit.id} value={credit.id}>{credit.name}</option>
                          ))}
                        </select>
                      </label>
                    ) : (
                      <label>
                        Account
                        <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                          <option value="">Select account</option>
                          {accounts.map((account) => (
                            <option key={account.id} value={account.id}>{account.name}</option>
                          ))}
                        </select>
                      </label>
                    )}
                    <label>
                      Month
                      <input type="month" value={statementMonth} onChange={(e) => setStatementMonth(e.target.value)} />
                    </label>
                  </div>
                  <div className="form-row">
                    <button className="login-button" type="submit" disabled={!canQuickAdd}>Save transaction</button>
                  </div>
                </form>
              </section>
            )}

            <div className="dashboard-split-grid">
              <section className="panel panel-featured dashboard-forecast-panel">
                <div className="section-header">
                  <div>
                    <h3>Account Forecast</h3>
                    <p className="panel-sub">Projected balances by month for the selected year.</p>
                  </div>
                  <label className="year-selector">
                    Year
                    <select
                      value={forecastYear}
                      onChange={(e) => setForecastYear(Number(e.target.value))}
                    >
                      {forecastYears.map((yearValue) => (
                        <option key={yearValue} value={yearValue}>{yearValue}</option>
                      ))}
                    </select>
                  </label>
                </div>
                {accountForecast.length === 0 ? (
                  <div className="empty-state">No forecast data yet.</div>
                ) : accounts.length === 0 ? (
                  <div className="empty-state">No accounts yet.</div>
                ) : visibleForecastAccounts.length === 0 ? (
                  <div className="empty-state">All accounts are hidden in forecast settings.</div>
                ) : (
                  <div className="forecast-table-wrap">
                    <div className="forecast-table-scroll">
                      <table className="forecast-table">
                        <thead>
                          <tr>
                            <th>Month</th>
                            {visibleForecastAccounts.map((account) => (
                              <th key={`forecast-head-${account.id}`}>{account.name}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {accountForecast.map((row) => {
                            const balanceLookup = buildBalanceLookup(row.balances);
                            return (
                              <tr key={row.month_key}>
                                <td>{row.month}</td>
                                {visibleForecastAccounts.map((account) => (
                                  <td key={`forecast-${row.month_key}-${account.id}`}>
                                    {formatCurrency(balanceLookup[normalizeForecastAccountName(account.name)] || 0)}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </section>

              <section className="panel panel-secondary dashboard-recent-panel">
                <div className="section-header">
                  <div>
                    <h3>Recent Transactions</h3>
                    <p className="panel-sub">Current month activity sorted by most recent.</p>
                  </div>
                  <input
                    type="month"
                    value={transactionMonth}
                    onChange={(e) => setTransactionMonth(e.target.value)}
                    className="transaction-month-input"
                  />
                </div>
                {sortedTransactions.length === 0 ? (
                  <div className="empty-state">No transactions this month.</div>
                ) : (
                  <div className="transactions-table-scroll">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Amount</th>
                          <th>Description</th>
                          <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedTransactions.slice(0, 10).map((row) => (
                          <tr key={row.id}>
                            <td>{new Date(row.occurred_at * 1000).toLocaleDateString()}</td>
                            <td className={`transaction-type transaction-${row.entry_type}`}>{row.entry_type}</td>
                            <td className={`transaction-amount transaction-${row.entry_type}`}>{formatCurrency(row.amount)}</td>
                            <td>{row.description || 'Untitled'}</td>
                            <td className="table-actions-cell">
                              <button type="button" onClick={() => handleDeleteTransaction(row.id)}>
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>

            <section className="panel panel-secondary">
              <div className="section-header">
                <div>
                  <h3>Credit Balances</h3>
                  <p className="panel-sub">Current balance across all credit options.</p>
                </div>
              </div>
              {credits.length === 0 ? (
                <div className="empty-state">No credit accounts yet.</div>
              ) : (
                <div className="credit-grid">
                  {credits.map((credit) => (
                    <article className="credit-card" key={credit.id}>
                      <div className="credit-card-header">
                        <span className="credit-name">{credit.name}</span>
                      </div>
                      <div className="credit-card-body">
                        <div className="credit-metric">
                          <span className="credit-label">Balance</span>
                          <span className="credit-value">{formatCurrency(credit.balance)}</span>
                        </div>
                        <div className="credit-metric">
                          <span className="credit-label">Limit</span>
                          <span className="credit-limit-value">{formatCurrency(credit.credit_limit)}</span>
                        </div>
                      </div>
                      {credit.credit_limit > 0 && (
                        <div className="credit-utilization">
                          <div className="utilization-bar">
                            <div 
                              className="utilization-fill" 
                              style={{ width: `${Math.min((credit.balance / credit.credit_limit) * 100, 100)}%` }}
                            ></div>
                          </div>
                          <span className="utilization-percent">
                            {Math.round((credit.balance / credit.credit_limit) * 100)}% utilized
                          </span>
                        </div>
                      )}
                    </article>
                  ))}
                </div>
              )}
              
              {/* Credit Payments by Month */}
              {credits.length > 0 && (
                <div className="credit-payments-section">
                  <h4>Payments by Month</h4>
                  <div className="payments-grid">
                    {credits.map((credit) => {
                      // Calculate charges by month
                      const chargesByMonth = {};
                      credit.charges.forEach((charge) => {
                        const month = charge.statement_month || '';
                        if (!chargesByMonth[month]) {
                          chargesByMonth[month] = 0;
                        }
                        chargesByMonth[month] += charge.amount || 0;
                      });

                      return (
                        <div className="payment-card" key={`payments-${credit.id}`}>
                          <div className="payment-card-title">{credit.name}</div>
                          <div className="payment-months">
                            {Object.keys(credit.paid_by_month).length > 0 || Object.keys(chargesByMonth).length > 0 ? (
                              (() => {
                                const allMonths = new Set([...Object.keys(credit.paid_by_month), ...Object.keys(chargesByMonth)]);
                                return Array.from(allMonths).sort().map((month) => {
                                  const charged = chargesByMonth[month] || 0;
                                  const paid = credit.paid_by_month[month] || 0;
                                  const remaining = charged - paid;

                                  return (
                                    <div key={`${credit.id}-${month}`}>
                                      <div className="payment-month-row">
                                        <span className="payment-month-name">{month}</span>
                                        <span className="payment-month-amount">{formatCurrency(paid)}</span>
                                      </div>
                                      {remaining > 0 && (
                                        <div className="payment-month-remaining">
                                          <span className="payment-remaining-label">Remaining</span>
                                          <span className="payment-remaining-amount">{formatCurrency(remaining)}</span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                });
                              })()
                            ) : (
                              <div className="payment-none">No charges or payments recorded</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>

          </>
        )}
      </section>
    </div>
  );
}

export default Dashboard;
